/**
 * Utility functions for calculating session time based on database timestamps.
 */

import { Session } from '@/types/session';

export function calculateRemainingSeconds(session: Session): number {
  if (!session || !session.duration) return 0;

  const totalDurationSeconds = session.duration * 60;

  // If we don't have a start timestamp, we cannot calculate elapsed time.
  if (!session.started_at) {
    return totalDurationSeconds;
  }

  // Terminal states
  if (session.status === 'completed' || session.status === 'cancelled' || session.status === 'stopped') {
    return 0;
  }

  const startTime = new Date(session.started_at).getTime();
  const now = Date.now();

  // Guard against invalid/future timestamps (treat as full duration; caller can decide to clear state)
  if (!Number.isFinite(startTime) || startTime > now) {
    return totalDurationSeconds;
  }

  // Paused sessions: calculate elapsed only up to paused_at
  if (session.status === 'paused') {
    if (!session.paused_at) {
      // If paused_at is missing, fall back to active-style calculation.
      const elapsedSeconds = Math.floor((now - startTime) / 1000);
      return Math.max(0, totalDurationSeconds - elapsedSeconds);
    }

    const pauseTime = new Date(session.paused_at).getTime();
    if (!Number.isFinite(pauseTime) || pauseTime < startTime) {
      return totalDurationSeconds;
    }

    const elapsedBeforePause = Math.floor((pauseTime - startTime) / 1000);
    return Math.max(0, totalDurationSeconds - elapsedBeforePause);
  }

  // Active sessions
  if (session.status === 'active') {
    const elapsedSeconds = Math.floor((now - startTime) / 1000);
    return Math.max(0, totalDurationSeconds - elapsedSeconds);
  }

  // Fallback: unknown state
  return totalDurationSeconds;
}

export function calculateElapsedSeconds(session: Session): number {
  const totalDurationSeconds = (session?.duration || 0) * 60;
  if (totalDurationSeconds <= 0) return 0;
  return Math.max(0, totalDurationSeconds - calculateRemainingSeconds(session));
}

/**
 * Calculate elapsed time in seconds for a session based on started_at timestamp
 * For active sessions, this should use the timer context instead
 */
export function calculateSessionElapsedTime(session: Session): number {
  return calculateElapsedSeconds(session);
}

/**
 * Calculate remaining time in seconds for a session
 */
export function calculateSessionRemainingTime(session: Session): number {
  return calculateRemainingSeconds(session);
}

/**
 * Calculate progress percentage for a session
 */
export function calculateSessionProgress(session: Session): number {
  const elapsed = calculateSessionElapsedTime(session);
  const totalDurationSeconds = session.duration * 60;

  if (totalDurationSeconds === 0) return 0;

  return Math.min(100, (elapsed / totalDurationSeconds) * 100);
}

/**
 * Check if a session is completed based on elapsed time
 */
export function isSessionCompleted(session: Session): boolean {
  return calculateSessionRemainingTime(session) <= 0;
}

/**
 * Format time as MM:SS
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
